<script setup>
import { computed } from "vue";
import DateTimeHelper from "@/common/DateTimeHelper";

const emit = defineEmits(["closed"]);

const props = defineProps({
  atsCandidate: {
    type: Object,
    required: true,
    default: () => {},
  },
});

const archivingTimeLabel = computed(() => {
  const reason = props.atsCandidate.archivingReason;
  switch (reason) {
    case "REJECTED":
      return "Rejected at";
    case "HIRED":
      return "Hired at";
    default:
      return "Archived at";
  }
});

function onClose() {
  emit("closed");
}

// A rejection is named by its disposition; an advance by the stage it moved the candidate to. A
// rejection's stageName is only the ATS's rejected stage ("Rejected"), so it adds nothing here.
function actionSummary(action) {
  if (action.action === "rejected") {
    return action.rejectReason ? `Rejected (${action.rejectReason})` : "Rejected";
  }
  return action.stageName ? `Advanced to ${action.stageName}` : "Advanced";
}

</script>

<template>
  <div id="ats-candidate">
    <div class="d-flex align-items-top justify-content-between">
      <div>
        <div>
          <span>Job: <b>{{ atsCandidate.atsJobName }}</b></span>
          <span v-if="atsCandidate.atsJobRequisitionRef"> (Req ID: {{ atsCandidate.atsJobRequisitionRef }})</span>
          <div v-if="atsCandidate.projectClosed">(closed)</div>
        </div>
        <div>Applied at: <b>{{ atsCandidate.appliedAt }}</b></div>
        <div>Source: <b>{{ atsCandidate.atsSource }}</b></div>

        <div v-if="atsCandidate.creditedTo">Credited to: <b>{{ atsCandidate.creditedTo }}</b></div>
        <div>Current stage: <b>{{ atsCandidate.stageName }}</b></div>
      </div>

      <div>
        <span
          v-if="atsCandidate.projectType !== 'prospect'"
          class="d-flex align-items-center"
        >
          <router-link :to="{ name: 'job-root', params: { projectId: atsCandidate.projectId } }">
            <el-button type="secondary">
              View job
            </el-button>
          </router-link>

          <span
            class="material-icons-round text-color-black-50 ml-8 md-18 close-touchpoint"
            @click="onClose"
          >
            close
          </span>
        </span>
      </div>
    </div>

    <div v-if="atsCandidate.answers?.length">Questions and answers:
      <div class="card-container mt-16">
        <div
          v-for="(qa, index) in atsCandidate.answers"
          :key="index"
          class="mb-16"
        >
          {{ qa.question }}<br>
          <b>{{ qa.answer || '- Not answered - '}}</b>
        </div>
      </div>
    </div>

    <template v-if="atsCandidate.archivingDescription">
      Disposition reason: <b>{{ atsCandidate.archivingDescription }}</b><br>
      {{ archivingTimeLabel }}: <b>{{ atsCandidate.archivingTime }}</b><br>
    </template>

    <!-- Who advanced or rejected this candidate from the jobs tab. The ATS credits every move to the
         integration user, so this is the only place the acting recruiter is shown. -->
    <div
      v-if="atsCandidate.stageActions?.length"
      class="mt-16"
    >
      <div>Stage actions in Teamable:</div>
      <div
        v-for="(action, index) in atsCandidate.stageActions"
        :key="index"
      >
        <b>{{ actionSummary(action) }}</b><span v-if="action.by"> by {{ action.by }}</span> on {{ DateTimeHelper.exactDateTimeInTimezone(action.appliedAt) }}
      </div>
    </div>
  </div>
</template>


<style scoped lang="scss">
.card-container {
  background-color: $black-5;
}
</style>